<template>
  <div>
    <page-title title="National Allergy Bureau" />
    <b-row>
      <b-col :lg="sideColumns">
        <div v-if="!isAuthenticated">
          <h2>My NAB</h2>
          <content-block name="SidebarPublicUser" v-if="!isAuthenticated" />
          <b-button variant="primary" @click="openAuthForm('signup')">Sign Up</b-button>
          <b-button variant="secondary" @click="openAuthForm('login')">Log In</b-button>
        </div>
        <div v-if="isAuthenticated">
          <div v-if="item && subscriptionWrappers && subscriptionWrappers.length > 0">
            <h2>My NAB</h2>
            <transition-group name="subscription-wrapper-list" tag="div">
              <div v-for="subscriptionWrapper in subscriptionWrappers" v-bind:key="subscriptionWrapper.subscriptionOrRole.id">
                <hr />
                <b-row>
                  <b-col xs="6">
                    <h3><b-link :to="'/station/' + subscriptionWrapper.subscriptionOrRole.station.id">{{subscriptionWrapper.subscriptionOrRole.station.displayCity}}, {{subscriptionWrapper.subscriptionOrRole.station.state}}</b-link></h3>
                    <b-link @click="onChangeTrigger(subscriptionWrapper)" v-if="subscriptionWrapper.subscription">{{getTriggerText(subscriptionWrapper.subscription.trigger)}}</b-link>
                  </b-col>
                  <b-col xs="6" class="text-right">
                    <h3 v-if="subscriptionWrapper.allergenCollectionSet">{{formatDateForUser(subscriptionWrapper.allergenCollectionSet.date)}}</h3>
                    <span class="home-subscription-wrapper-icon" v-if="subscriptionWrappers.length > 1 && subscriptionWrapper.displayWeight != 0"><b-link @click="onStationMovedUp(subscriptionWrapper.subscription.id)" v-if="subscriptionWrapper.subscription"><font-awesome-icon icon="arrow-up" /></b-link></span>
                    <span class="home-subscription-wrapper-icon" v-if="subscriptionWrappers.length > 1 && subscriptionWrapper.displayWeight != maxDisplayWeight"><b-link @click="onStationMovedDown(subscriptionWrapper.subscription.id)" v-if="subscriptionWrapper.subscription"><font-awesome-icon icon="arrow-down" /></b-link></span>
                    <span class="home-subscription-wrapper-icon"><b-link @click="onStationRemoved(subscriptionWrapper.subscription.id)" v-if="subscriptionWrapper.subscription"><font-awesome-icon icon="trash" /></b-link></span>
                  </b-col>
                </b-row>
                <div v-if="!subscriptionWrapper.allergenCollectionSet">No allergen collections are available for this station.</div>
                <ul v-if="subscriptionWrapper.role">
                  <li><router-link to="/allergenCollectionSet/add">Add Collection Data</router-link></li>
                  <li><router-link to="/allergenCollectionSet/import">Bulk Upload Collection Data</router-link></li>
                  <li><router-link to="/allergenCollectionSet">View Collection Data</router-link></li>
                  <li><router-link to="/report/adHoc">Export Collection Data</router-link></li>
                  <li v-if="subscriptionWrapper.role.type == 'STATION_HEAD'"><router-link :to="'/station/' + subscriptionWrapper.role.station.id + '/edit'">Administer Station</router-link></li>
                  <li v-if="subscriptionWrapper.role.type == 'STATION_COLLECTOR'"><router-link :to="'/station/' + subscriptionWrapper.role.station.id + '/edit'">View Station</router-link></li>
                </ul>
                <allergen-collection-set-card v-if="subscriptionWrapper.allergenCollectionSet" :station="subscriptionWrapper.subscriptionOrRole.station" :allergenCollectionSet="subscriptionWrapper.allergenCollectionSet"></allergen-collection-set-card>
              </div>
            </transition-group>
          </div>
          <div v-if="item && (!subscriptionWrappers || subscriptionWrappers.length == 0)">
            <content-block name="SidebarNoSubscriptions" />
          </div>
        </div>
        <div v-if="isSiteAdmin">
          <b-card>
            <template v-slot:header>
              <h3>NAB Site Administrator</h3>
            </template>
            <b-card-text>
              <h4>Data Management</h4>
              <ul>
                <li><b-link to="/station">Manage Stations</b-link></li>
                <li><b-link to="/allergen">Manage Allergens</b-link></li>
                <li><b-link to="/allergenCollectionSet">Manage Collections</b-link></li>
                <li><b-link to="/scaleSet/master">Edit Master Concentration Levels</b-link></li>
                <li><b-link to="/applicationUser">Manage Users</b-link></li>
                <li><b-link to="/contentBlock">Manage Content</b-link></li>
                <li><b-link to="/report/adHoc">Ad-Hoc Reporting</b-link></li>
              </ul>
              <h4>System Administration</h4>
              <ul>
                <li><b-link to="/testing">Testing Notes</b-link></li>
                <li><b-link to="/hangfire">Hangfire UI</b-link></li>
                <li><b-link to="/ui/playground">GraphQL Playground</b-link></li>
              </ul>
            </b-card-text>
          </b-card>
        </div>
      </b-col>
      <b-col :lg="mapColumns">
        <content-block name="WelcomePublicUser" v-if="!isAuthenticated" />
        <content-block name="WelcomeAuthenticatedUser" v-if="isAuthenticated" />
        <map-widget @stationAdded="onStationAdded" @subscriptionUpdated="onSubscriptionUpdated" @stationRemoved="onStationRemoved" :subscriptionWrappers="subscriptionWrappers"></map-widget>
      </b-col>
    </b-row>
    <b-modal id="changeTriggerModal" :title="changeTriggerModalTitle" header-bg-variant="primary" header-text-variant="light" @ok="onChangeTriggerSubmit">
      <b-form novalidate @submit.prevent>
        <select-control v-if="changeTriggerValue" label="Email Alert Trigger" description="You can receive email alerts whenever new allergen counts are available for this station. Or you can choose to just have this station occur on your My NAB dashboard." v-model="changeTriggerValue" :options="triggers" required></select-control>
      </b-form>
    </b-modal>
    <auth-modal :authBus="authBus" @onAuthComplete="onAuthComplete"></auth-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import CrudOperationsMixin from './Common/CrudOperationsMixin.js'
import DateTimeMixins from './Common/DateTimeMixins.js'
import SubscriptionMixins from './Common/SubscriptionMixins.js'
import Constants from './Common/Constants.js'

export default {
  name: 'Home',
  mixins: [
    CrudOperationsMixin,
    DateTimeMixins,
    SubscriptionMixins
  ],
  props: {},
  data() {
    return {
      item: null,
      subscriptionWrappers: [],
      changeTriggerSubscriptionWrapper: null,
      changeTriggerValue: null,
      authBus: new Vue(),
      authFormStationAddedData: null
    };
  },
  computed: {
    isAuthenticated: function () {
      return this.$root.$data.authenticatedUser.id && this.$root.$data.authenticatedUser.username.length > 0;
    },
    mapColumns: function () {
      if (!this.isAuthenticated)
        return 9;
      
      return 6;
    },
    sideColumns: function () {
      return 12 - this.mapColumns;
    },
    isSiteAdmin: function () {
      return this.$root.$data.authenticatedUser && this.$root.$data.authenticatedUser.roles && this.$root.$data.authenticatedUser.roles.includes('SiteAdmin');
    },
    changeTriggerModalTitle: function () {
      if (this.changeTriggerSubscriptionWrapper)
        return this.changeTriggerSubscriptionWrapper.subscriptionOrRole.station.displayCity + ' Email Alerts';
      else
        return 'Email Alerts';
    },
    maxDisplayWeight: function () {
      return Math.max.apply(Math, this.subscriptionWrappers.map(function(x) { return x.displayWeight; }))
    }
  },
  methods: {
    openAuthForm: function (mode) {
      this.authBus.$emit('openAuthForm', mode);
    },
    onChangeTriggerSubmit: function () {
      this.onSubscriptionUpdated(this.changeTriggerSubscriptionWrapper.subscriptionOrRole.id, this.changeTriggerValue);
    },
    onChangeTrigger: function (subscriptionWrapper) {
      this.changeTriggerSubscriptionWrapper = subscriptionWrapper;
      this.changeTriggerValue = this.changeTriggerSubscriptionWrapper.subscriptionOrRole.trigger;
      this.$bvModal.show('changeTriggerModal');
    },
    onSubscriptionUpdated: function (id, trigger) {
      let existingSubscriptionWrapper = this.subscriptionWrappers.find(x => x.subscriptionOrRole.id == id && x.type == 'subscription');
      if (!existingSubscriptionWrapper)
        return;

      this.updateSubscription(id, trigger)
        .then((subscription) => {
          this.subscriptionWrappers = this.subscriptionWrappers.filter(x => {
            return !x.subscription || x.subscription.id != id;
          });
          this.loadSubscription(subscription);
        });
    },
    onStationAdded: function (id, trigger, isRetry) {
      let existingSubscriptionWrapper = this.subscriptionWrappers.find(x => x.subscriptionOrRole.station.id == id && x.type == 'subscription');
      if (existingSubscriptionWrapper) {
        this.$parent.showToast(existingSubscriptionWrapper.subscriptionOrRole.station.displayCity + ' is already one of your My NAB stations, so it cannot be added again.', {
          title: 'Cannot Add Station',
          variant: 'info',
          autoHideDelay: 5000
        });
        return;
      }

      if (!this.isAuthenticated) {
        if (isRetry)
          return;

        this.authFormStationAddedData = {
          id: id,
          trigger: trigger
        };
        
        this.openAuthForm('signup');
      } else {

        this.addSubscription(id, trigger)
          .then(subscription => {
            if (isRetry)
              this.load();
            else
              this.loadSubscription(subscription);
          })
      }
    },
    onStationRemoved: function (id) {
      let existingSubscriptionWrapper = this.subscriptionWrappers.find(x => x.subscriptionOrRole.id == id && x.type == 'subscription');
      if (!existingSubscriptionWrapper)
        return;

      this.removeSubscription(existingSubscriptionWrapper.subscriptionOrRole, existingSubscriptionWrapper.subscriptionOrRole.station)
        .then(() => {
          this.load();
        });
    },
    onStationMovedUp: function (id) {
      let existingSubscriptionWrapper = this.subscriptionWrappers.find(x => x.subscriptionOrRole.id == id && x.type == 'subscription');
      if (!existingSubscriptionWrapper)
        return;

      this.moveUp(id)
        .then(() => {
          this.load();
        });
    },
    onStationMovedDown: function (id) {
      let existingSubscriptionWrapper = this.subscriptionWrappers.find(x => x.subscriptionOrRole.id == id && x.type == 'subscription');
      if (!existingSubscriptionWrapper)
        return;

      this.moveDown(id)
        .then(() => {
          this.load();
        });
    },
    onAuthComplete: function () {
      // TODO: I think there's still some sort of race condition in here. If you aren't logged in, click the map info window button to add a station, and go through the auth form -- it often seems to work correctly, but sometimes doesn't seem to do a refresh after loading the newly added subscription.
      if (this.authFormStationAddedData) {
        this.onStationAdded(this.authFormStationAddedData.id, this.authFormStationAddedData.trigger, true);
        this.authFormStationAddedData = null;
      }
    },
    load: function () {
      if (!this.isAuthenticated)
        return;

      this.subscriptionWrappers = [];

      this.readQuery('/graphql/public', {
        query: `
          query ($id: ID) {
            applicationUser (id: $id) {
              id
              userName
              subscriptions {
                id,
                trigger,
                displayWeight,
                station {
                  id
                  name
                  displayCity
                  state
                  postalCode
                  country
                  scaleSet {
                    id,
                    scales {
                      id,
                      category,
                      range1Begin,
                      range2Begin,
                      range3Begin,
                      range4Begin
                    }
                  }
                }
              }
              roles {
                id
                type
                station {
                  id
                  name
                  displayCity
                  state
                  postalCode
                  country
                  scaleSet {
                    id,
                    scales {
                      id,
                      category,
                      range1Begin,
                      range2Begin,
                      range3Begin,
                      range4Begin
                    }
                  }
                }
              }
            }
          }`,
          variables: {
            'id': this.$root.$data.authenticatedUser.id
          }
        }, (data) => {
          this.item = data.applicationUser;

          this.item.subscriptions.forEach(s => {
            this.loadSubscription(s);
          });

          this.item.roles.forEach(r => {
            this.readQuery('/graphql/public', {
              query: `
                query {
                  allergenCollectionSets (limit: 1, filter: "station.id=\\"` + r.station.id + `\\"", order: "date desc") {
                    id,
                    date
                    isCommentPublic,
                    comment,
                    weatherNotes,
                    allergenCollections {
                      id,
                      value,
                      allergen {
                        id,
                        name,
                        commonName,
                        family,
                        category
                      }
                    }
                  }
                }`
              }, (data) => {
                this.addSubscriptionWrapper({
                  role: r,
                  subscriptionOrRole: r,
                  type: 'role',
                  allergenCollectionSet: data.allergenCollectionSets[0],
                  displayWeight: -1
              });
            });
          });

        });
    },
    loadSubscription: function (subscription) {
      this.readQuery('/graphql/public', {
        query: `
          query {
            allergenCollectionSets (limit: 1, filter: "station.id=\\"` + subscription.station.id + `\\"", order: "date desc") {
              id,
              date
              isCommentPublic,
              comment,
              weatherNotes,
              allergenCollections {
                id,
                value,
                allergen {
                  id,
                  name,
                  commonName,
                  family,
                  category
                }
              }
            }
          }`
        }, (data) => {
          this.addSubscriptionWrapper({
            subscription: subscription,
            subscriptionOrRole: subscription,
            type: 'subscription',
            allergenCollectionSet: data.allergenCollectionSets[0],
            displayWeight: subscription.displayWeight
          });
        });
    },
    addSubscriptionWrapper: function (subscriptionWrapper) {
      this.subscriptionWrappers.push(subscriptionWrapper);
      this.subscriptionWrappers.sort((a, b) => a.displayWeight - b.displayWeight);
    }
  },
  mounted () {
    this.load();
    // Use this instead of the previous line to test the "Loading" bar
    //window.setTimeout(this.load, 3000);
  }
}
</script>